import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import apiClient from "../../api/apiClient"
import { API_ENDPOINTS } from "../../constants/apiEndpoints"
import { toast } from "react-hot-toast"

// Helper function to get user from sessionStorage
const getUserFromStorage = () => {
  const user = sessionStorage.getItem("user")
  return user ? JSON.parse(user) : null
}

// Async Thunk for Signup
export const signupUser = createAsyncThunk("auth/signupUser", async (userData, { rejectWithValue }) => {
  try {
    const response = await apiClient.post(API_ENDPOINTS.SIGNUP, userData)
    return response.data
  } catch (error) {
    // Extract the error message properly
    const errorMessage = error.response?.data?.message || error.response?.data || "Signup failed"
    toast.error(errorMessage)
    console.error("Signup error:", errorMessage)
    console.log("Error details:", error)
    return rejectWithValue(errorMessage)
  }
})

// Async Thunk for Login
export const loginUser = createAsyncThunk("auth/loginUser", async (credentials, { rejectWithValue }) => {
  try {
    const response = await apiClient.post(API_ENDPOINTS.LOGIN, credentials)
    const token = response.data.token
    const user = response.data.user

    // Save token and user in sessionStorage
    sessionStorage.setItem("authToken", token)
    sessionStorage.setItem("user", JSON.stringify(user))

    toast.success("Login successful!")
    return { user, token }
  } catch (error) {
    // Extract the error message properly
    const errorMessage = error.response?.data?.message || error.response?.data || "Login failed"
    toast.error(errorMessage)
    return rejectWithValue(errorMessage)
  }
})

// Async Thunk for Google Auth
export const googleAuth = createAsyncThunk("auth/googleAuth", async ({ tokenId }, { rejectWithValue }) => {
  try {
    const response = await apiClient.post(API_ENDPOINTS.GOOGLE_LOGIN, { tokenId })
    const token = response.data.token
    const user = response.data.user

    // Save token and user in sessionStorage
    sessionStorage.setItem("authToken", token)
    sessionStorage.setItem("user", JSON.stringify(user))

    toast.success("Google login successful!")
    return response.data
  } catch (error) {
    // Extract the error message properly
    const errorMessage = error.response?.data?.message || error.response?.data || "Google login failed"
    toast.error(errorMessage)
    return rejectWithValue(errorMessage)
  }
})

// Async Thunk for Forgot Password
export const forgotPassword = createAsyncThunk("auth/forgotPassword", async (email, { rejectWithValue }) => {
  try {
    const response = await apiClient.post(API_ENDPOINTS.FORGOT_PASSWORD, { email })
    toast.success("Password reset email sent successfully!")
    return response.data
  } catch (error) {
    // Extract the error message properly
    const errorMessage = error.response?.data?.message || error.response?.data || "Password reset failed"
    toast.error(errorMessage)
    return rejectWithValue(errorMessage)
  }
})

// Async Thunk for OTP Verification
export const verifyOtp = createAsyncThunk("auth/verifyOtp", async ({ otp, email }, { rejectWithValue }) => {
  try {
    const response = await apiClient.post(API_ENDPOINTS.OTP_VERIFCATION, { otp, email })
    toast.success("OTP verified successfully!")
    return response.data
  } catch (error) {
    // Extract the error message properly
    const errorMessage = error.response?.data?.message || error.response?.data || "OTP verification failed"
    toast.error(errorMessage)
    return rejectWithValue(errorMessage)
  }
})

// Async Thunk for Change Password
export const changePassword = createAsyncThunk("auth/changePassword", async (passwordData, { rejectWithValue }) => {
  try {
    const response = await apiClient.put(API_ENDPOINTS.CHANGE_PASSWORD, passwordData)
    toast.success("Password changed successfully!")
    return response.data
  } catch (error) {
    // Extract the error message properly
    const errorMessage = error.response?.data?.message || error.response?.data || "Password change failed"
    toast.error(errorMessage)
    return rejectWithValue(errorMessage)
  }
})

// Async Thunk for OTP Verification
export const verifyOtpforsignup = createAsyncThunk("auth/verifyOtpForSignup", async ({ otp, email, password, fullName, dob, gender }, { rejectWithValue }) => {
  try {
    const response = await apiClient.post(API_ENDPOINTS.OTP_VERIFCATION_SIGNUP, { otp, email, password, fullName, dob, gender });
    const token = response.data.token
    const user = response.data.user

    // Save token and user in sessionStorage
    sessionStorage.setItem("authToken", token)
    sessionStorage.setItem("user", JSON.stringify(user))
    return response.data;
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.response?.data || "OTP verification failed";
    toast.error(errorMessage);
    return rejectWithValue(errorMessage);
  }
});

export const logoutUser = createAsyncThunk("auth/logoutUser", async (_, { rejectWithValue }) => {
  try {
    // Clear sessionStorage on logout
    sessionStorage.removeItem("authToken");
    sessionStorage.removeItem("user");

    // Reload the page
    window.location.reload(); // Reload the whole page to reset everything
    return true;
  } catch (error) {
    const errorMessage = error.response?.data?.message || "Logout failed";
    toast.error(errorMessage);
    return rejectWithValue(errorMessage);
  }
});



const authSlice = createSlice({
  name: "auth",
  initialState: {
    user: getUserFromStorage(),
    token: sessionStorage.getItem("authToken") || null,
    loading: false,
    error: null,
    isOtpModalOpen: false, // Add state for OTP modal
    otpEmail: "", // Store email for OTP verification
  },
  reducers: {
    logout: (state) => {
      state.user = null
      state.token = null
      sessionStorage.removeItem("authToken")
      sessionStorage.removeItem("user")
      toast.success("Logged out successfully")
    },
    openOtpModal: (state, action) => {
      state.isOtpModalOpen = true
      state.otpEmail = action.payload
    },
    closeOtpModal: (state) => {
      state.isOtpModalOpen = false
      state.otpEmail = ""
    },
    // ✅ Update user reducer
    updateUser: (state, action) => {
      state.user = {
        ...state.user,
        ...action.payload,
      }
      sessionStorage.setItem("user", JSON.stringify(state.user))
    },

  },
  extraReducers: (builder) => {
    builder
      .addCase(signupUser.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(signupUser.fulfilled, (state, action) => {
        state.loading = false
        state.user = action.payload.user
        state.token = action.payload.token
        // If signup requires OTP verification, handle it here
        if (action.payload.requiresOtp) {
          state.isOtpModalOpen = true
          state.otpEmail = action.payload.user.email
        }
      })
      .addCase(signupUser.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      // Login Cases
      .addCase(loginUser.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false
        state.user = action.payload.user
        state.token = action.payload.token
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      // Google Auth Cases
      .addCase(googleAuth.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(googleAuth.fulfilled, (state, action) => {
        state.loading = false
        state.user = action.payload.user
        state.token = action.payload.token
      })
      .addCase(googleAuth.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      .addCase(verifyOtpforsignup.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(verifyOtpforsignup.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.isOtpModalOpen = false; // Close the OTP modal on success
        toast.success("Signup successful!");
      })
      .addCase(verifyOtpforsignup.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
  },
})

export const { logout, openOtpModal, closeOtpModal, updateUser } = authSlice.actions
export default authSlice.reducer
